import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { nutritionAPI, planDocumentAPI } from '../../services/api'
import NewFoodForm from './NewFoodForm'
import { contentLanguage } from '../../i18n/supported'

const uiLanguage = contentLanguage
const CONFIDENCE_STYLE = {
  high: 'bg-green-50 text-green-800 border-green-200',
  medium: 'bg-amber-50 text-amber-800 border-amber-200',
  low: 'bg-red-50 text-red-800 border-red-200',
}

// Initial choice of a row: the saved mapping wins, then the suggestion (spices → skip).
function initialChoice(row) {
  const saved = row.mapping
  if (saved?.skip) return { skip: true, source: 'saved' }
  if (saved?.food_id) {
    return { food_id: saved.food_id, name: saved.display_name || saved.food_name, grams: saved.grams, skip: false,
      source: saved.source === 'plan' ? 'plan' : 'saved', created: !!saved.created_from_plan }
  }
  const s = row.suggestion
  if (s?.skip) return { skip: true, source: 'spice', grams: row.grams }
  if (s?.food_id) {
    return { food_id: s.food_id, name: s.display_name || s.food_name, grams: row.grams, skip: false,
      source: s.source, confidence: s.confidence }
  }
  return { food_id: null, name: null, grams: row.grams, skip: false }
}

const complete = (c) => c.skip || ((c.food_id || c.create) && Number(c.grams) > 0)

function FoodSearch({ cycleId, planId, language, initialQuery, ingredientName, onPick, onCancel }) {
  const { t } = useTranslation()
  const [q, setQ] = useState(initialQuery)
  const [creating, setCreating] = useState(false)
  const search = useQuery({
    queryKey: ['planMappingFoodSearch', planId, q, language],
    queryFn: () => planDocumentAPI.searchMappingFoods(cycleId, planId, q, language).then((r) => r.data),
    enabled: q.trim().length >= 2,
  })
  return (
    <div className="mt-1 rounded border border-gray-300 bg-surface-container p-2 space-y-1" data-testid="mapping-food-search">
      <div className="flex gap-2">
        <input autoFocus className="border rounded px-2 py-1 text-sm flex-1 min-w-0" value={q}
          placeholder={t('planMeals.bulk.searchPlaceholder')} aria-label={t('planMeals.bulk.searchPlaceholder')}
          onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="text-sm underline" onClick={onCancel}>{t('mealPrep.actions.cancel')}</button>
      </div>
      {search.isFetching && <p className="text-xs text-gray-500">{t('planMeals.bulk.searching')}</p>}
      {creating && (
        <NewFoodForm initialName={ingredientName || q.trim()} onCancel={() => setCreating(false)}
          onCreated={(food) => onPick({ id: food.id, name: food.name, display_name: food.name, uses: 0, created: true })} />
      )}
      {!creating && search.data?.length === 0 && <p className="text-xs text-gray-500">{t('planMeals.bulk.noResults')}</p>}
      <ul className="max-h-48 overflow-y-auto" hidden={creating}>
        {(search.data ?? []).map((food) => (
          <li key={food.id}>
            <button type="button" className="w-full text-left text-sm px-2 py-1 rounded hover:bg-gray-100"
              onClick={() => onPick(food)}>
              {food.display_name}{food.brand ? ` (${food.brand})` : ''}
              {food.uses > 0 && <span className="ml-2 text-xs text-gray-500">{t('planMeals.bulk.source.history')}</span>}
            </button>
          </li>
        ))}
      </ul>
      {!creating && (ingredientName || q.trim()).length >= 2 && (
        <button type="button" className="w-full text-left text-sm px-2 py-1 rounded border border-dashed border-gray-400 hover:bg-gray-100"
          data-testid="mapping-create-food" onClick={() => setCreating(true)}>
          + {t('planMeals.bulk.createNew', { name: ingredientName || q.trim() })}
        </button>
      )}
    </div>
  )
}

// VH-229 refinement: one dialog for all plan ingredients that still need a food — prefilled suggestions,
// editable grams, skip toggles; "Apply all" saves every mapping at once and (when logging) logs right after.
export function PlanMappingDialog({ cycleId, planId, slots = null, meals = [], logAfterSave = false, onClose, onSaved }) {
  const { t, i18n } = useTranslation()
  const language = uiLanguage(i18n.language)
  const [choices, setChoices] = useState({})
  const [searching, setSearching] = useState(null)
  const [showMapped, setShowMapped] = useState(!logAfterSave)
  const [translated, setTranslated] = useState({})
  const [highlight, setHighlight] = useState(null)

  const suggestions = useQuery({
    queryKey: ['planMappingSuggestions', planId, language],
    queryFn: () => planDocumentAPI.mappingSuggestions(cycleId, planId, language).then((r) => r.data),
    gcTime: 0,
  })

  const rows = useMemo(() => (suggestions.data?.ingredients ?? [])
    .filter((r) => !slots || slots.includes(r.slot)), [suggestions.data, slots])

  useEffect(() => {
    if (!suggestions.data) return
    setChoices(Object.fromEntries(rows.map((r) => [r.key, initialChoice(r)])))
  }, [suggestions.data, rows])

  // Food names are stored in English; translate the ones without a cached translation (on demand, cached).
  const foodIds = useMemo(() => [...new Set(Object.values(choices).map((c) => c.food_id).filter(Boolean))], [choices])
  useEffect(() => {
    const missing = foodIds.filter((id) => !translated[id])
    if (language === 'en' || missing.length === 0) return
    Promise.resolve()
      .then(() => nutritionAPI.translateFoodNamesBatch(missing, language))
      .then((r) => setTranslated((prev) => ({ ...prev, ...(r.data?.translations ?? {}) })))
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps -- translated would re-trigger the request
  }, [foodIds, language])

  const save = useMutation({
    mutationFn: (mappings) => planDocumentAPI.setMappings(cycleId, planId, mappings),
    onSuccess: () => onSaved?.(),
  })

  const update = (key, patch) => setChoices((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }))
  const needsMapping = (r) => !r.mapping
  const visible = rows.filter((r) => showMapped || needsMapping(r))
  const mappedCount = rows.filter((r) => !needsMapping(r)).length
  const toSave = rows.filter((r) => choices[r.key] && (needsMapping(r) || showMapped))
  const incompleteRows = toSave.filter((r) => !complete(choices[r.key]))
  const missing = incompleteRows.length
  const cleanName = (r) => String(r.name || '').replace(/\*/g, '')
  const showRow = (r) => {
    document.querySelector(`[data-testid="mapping-row-${r.key}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    setHighlight(r.key)
    setTimeout(() => setHighlight((k) => (k === r.key ? null : k)), 2000)
  }
  const bySlot = visible.reduce((acc, r) => ({ ...acc, [r.slot]: [...(acc[r.slot] ?? []), r] }), {})
  const mealTotals = (slot) => meals.find((m) => m.slot === slot)?.totals ?? {}

  const apply = () => {
    const mappings = toSave.map((r) => {
      const c = choices[r.key]
      if (c.skip) return { slot: r.slot, index: r.index, skip: true }
      if (c.create) return { slot: r.slot, index: r.index, create_from_plan: true, grams: Number(c.grams) }
      return { slot: r.slot, index: r.index, food_id: c.food_id, grams: Number(c.grams) }
    })
    if (!mappings.length) { onSaved?.(); return }
    save.mutate(mappings)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="plan-mapping-title" data-testid="plan-mapping-dialog">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative mx-auto my-4 sm:my-10 w-[calc(100%-2rem)] max-w-4xl rounded-lg bg-surface-container shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 p-4">
          <div>
            <h2 id="plan-mapping-title" className="text-lg font-semibold text-gray-900">{t('planMeals.bulk.title')}</h2>
            <p className="text-sm text-gray-600">{t('planMeals.bulk.intro')}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('planMeals.bulk.close')} className="text-gray-500 hover:text-gray-800">
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {suggestions.isLoading && <p className="text-sm text-gray-600">{t('planMeals.bulk.loading')}</p>}
          {suggestions.isError && <p className="text-sm text-red-700" role="alert">{t('planMeals.bulk.errors.load')}</p>}
          {suggestions.data && mappedCount > 0 && logAfterSave && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={showMapped} onChange={(e) => setShowMapped(e.target.checked)} />
              {t('planMeals.bulk.showMapped', { count: mappedCount })}
            </label>
          )}
          {suggestions.data && visible.length === 0 && <p className="text-sm text-gray-700">{t('planMeals.bulk.nothingToDo')}</p>}

          {Object.entries(bySlot).map(([slot, list]) => (
            <section key={slot} className="space-y-2" data-testid={`mapping-slot-${slot}`}>
              <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-1">
                {t('planMeals.bulk.slotAs', { slot: t(`weeklyPlan.slot.${slot}`), mealType: t(`nutrition.mealTypes.${list[0].meal_type}`) })}
              </h3>
              <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">
                <span className="col-span-3">{t('planMeals.bulk.ingredient')}</span>
                <span className="col-span-5">{t('planMeals.bulk.food')}</span>
                <span className="col-span-2">{t('planMeals.bulk.grams')}</span>
                <span className="col-span-2">{t('planMeals.bulk.skip')}</span>
              </div>
              {list.map((row) => {
                const c = choices[row.key] ?? initialChoice(row)
                const incomplete = !complete(c)
                const totals = mealTotals(slot)
                return (
                  <div key={row.key} data-testid={`mapping-row-${row.key}`}
                    className={`grid grid-cols-1 sm:grid-cols-12 gap-2 rounded border p-2 text-sm ${incomplete ? 'border-amber-300 bg-amber-50' : 'border-gray-200'} ${highlight === row.key ? 'ring-2 ring-amber-500' : ''}`}>
                    <div className="sm:col-span-3">
                      <p className="font-medium text-gray-900">{String(row.name || '').replace(/\*/g, '')}</p>
                      <p className="text-xs text-gray-600">{row.amount || '—'}</p>
                    </div>
                    <div className="sm:col-span-5 min-w-0">
                      {c.skip ? (
                        <p className="text-gray-500 italic">{t(`planMeals.bulk.source.${c.source === 'spice' ? 'spice' : 'saved'}`)}</p>
                      ) : c.create ? (
                        <p className="text-gray-800">{t('planMeals.bulk.created')}</p>
                      ) : c.food_id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-gray-900">{translated[c.food_id] || c.name}</span>
                          {c.confidence && (
                            <span className={`text-[11px] rounded-full border px-2 ${CONFIDENCE_STYLE[c.confidence]}`}>
                              {t(`planMeals.bulk.confidence.${c.confidence}`)}
                            </span>
                          )}
                          {c.source && <span className="text-xs text-gray-500">{t(`planMeals.bulk.source.${c.source}`)}</span>}
                        </div>
                      ) : (
                        <p className="text-amber-800">{t('planMeals.bulk.noSuggestion')}</p>
                      )}
                      {!c.skip && searching !== row.key && (
                        <button type="button" className="text-xs underline text-gray-700" data-testid={`mapping-change-${row.key}`}
                          onClick={() => setSearching(row.key)}>
                          {c.food_id || c.create ? t('planMeals.bulk.change') : t('planMeals.bulk.choose')}
                        </button>
                      )}
                      {searching === row.key && (
                        <FoodSearch cycleId={cycleId} planId={planId} language={language}
                          initialQuery={String(row.search_name || row.name || '').replace(/\*/g, '').split(/[,(]/)[0].trim()}
                          ingredientName={String(row.name_en || row.name || '').replace(/\*/g, '').split('(')[0].trim()}
                          onCancel={() => setSearching(null)}
                          onPick={(food) => {
                            update(row.key, { food_id: food.id, name: food.display_name, create: false, confidence: null, source: food.created ? 'created' : (food.uses > 0 ? 'history' : 'database'), created: !!food.created })
                            setSearching(null)
                          }} />
                      )}
                      {row.can_create_from_plan && !c.skip && totals.kcal > 0 && (
                        <label className="mt-1 flex items-start gap-2 text-xs text-gray-700" title={t('planMeals.bulk.createFromPlanHint')}>
                          <input type="checkbox" checked={!!c.create} data-testid={`mapping-create-${row.key}`}
                            onChange={(e) => update(row.key, { create: e.target.checked })} />
                          <span>{t('planMeals.bulk.createFromPlan', { kcal: Math.round(totals.kcal) })}</span>
                        </label>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <input type="number" min="1" max="3000" step="any" disabled={c.skip}
                        className="border rounded px-2 py-1 text-sm w-24 disabled:bg-gray-100"
                        aria-label={t('planMeals.bulk.grams')} data-testid={`mapping-grams-${row.key}`}
                        value={c.grams ?? ''} placeholder="g" title={t('planMeals.bulk.gramsUnknown')}
                        onChange={(e) => update(row.key, { grams: e.target.value === '' ? null : Number(e.target.value) })} />
                      {row.grams_basis && row.grams_basis !== 'amount' && row.unit_g && (
                        <p className="text-[11px] text-gray-500">
                          {t(`planMeals.bulk.basis.${row.grams_basis}`, { count: row.unit_count, unit: row.unit_g })}
                        </p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input type="checkbox" checked={!!c.skip} data-testid={`mapping-skip-${row.key}`}
                          onChange={(e) => update(row.key, { skip: e.target.checked })} />
                        {t('planMeals.bulk.skip')}
                      </label>
                    </div>
                  </div>
                )
              })}
            </section>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 p-4">
          {missing > 0 && (
            <p className="text-sm text-amber-800 mr-auto" role="status" data-testid="mapping-missing">
              {t('planMeals.bulk.stillMissing')}{' '}
              {incompleteRows.map((r, i) => (
                <span key={r.key}>
                  {i > 0 && ', '}
                  <button type="button" className="underline font-medium" data-testid={`mapping-missing-${r.key}`}
                    onClick={() => showRow(r)}>{cleanName(r)}</button>
                </span>
              ))}
            </p>
          )}
          {save.isError && <p className="text-sm text-red-700 mr-auto" role="alert">{t('planMeals.bulk.errors.save')}</p>}
          <button type="button" className="btn-outline text-sm" onClick={onClose}>{t('mealPrep.actions.cancel')}</button>
          <button type="button" className="btn-primary text-sm" data-testid="mapping-apply-all"
            disabled={!suggestions.data || missing > 0 || save.isPending} onClick={apply}
            title={missing > 0 ? `${t('planMeals.bulk.stillMissing')} ${incompleteRows.map(cleanName).join(', ')}` : undefined}>
            {t('planMeals.bulk.applyAll')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default PlanMappingDialog
